import { handleActions } from "redux-actions";
import { Map, fromJS } from "immutable";

import {
    CATALOGS_FAILURE,
    CATALOGS_FILTERS,
    CATALOGS_REQUEST,
    CATALOGS_SUCCESS,
    PAGE_LEAVE,
    BRAND_PROFILE_CATALOGS_SUCCESS,
    CATALOGS_RESET,
    FEATURED_CATALOGS_COMPLETED,
    CATALOGS,
    CATALOG,
} from "constants/action-types";

interface IState {
    entities: any;
    result: any[];
    filters: any;
    isFetching: boolean;
    isFetchingCache: boolean;
    error: any;
    initialized?: boolean;
    featuredEntities?: any;
    featuredResult?: any[];
    hasMoreResults: boolean;
    nextPage?: string;
    productCache: Map<any, any>;
}

const initialState: IState = {
    entities: {},
    result: [],
    filters: Map(),
    isFetching: false,
    isFetchingCache: false,
    error: undefined,
    featuredEntities: {},
    featuredResult: [],
    hasMoreResults: false,
    nextPage: undefined,
    productCache: Map(),
};

const catalogsReducer = handleActions(
    {
        [CATALOGS_REQUEST]: (state) => {
            return {
                ...state,
                isFetching: true,
                error: undefined,
            };
        },
        [CATALOGS_SUCCESS]: (state: IState, { data }: any): IState => {
            const entities = data.entities || {};
            let result = [];
            if (data.result) {
                result = Array.isArray(data.result) ? data.result : [data.result];
            }
            return {
                ...state,
                isFetching: false,
                entities: {
                    ...state.entities,
                    ...entities,
                },
                result: [...state.result, ...result],
            };
        },
        [CATALOGS_FAILURE]: (state, error) => {
            return {
                ...initialState,
                filters: state.filters,
                error,
            };
        },
        [CATALOGS_FILTERS]: (state, { payload }) => {
            return {
                ...state,
                filters: payload,
            };
        },
        [PAGE_LEAVE]: () => {
            return initialState;
        },
        [BRAND_PROFILE_CATALOGS_SUCCESS]: (state, { payload }) => {
            return {
                ...state,
                initialized: true,
                isFetching: false,
                result: Array.isArray(payload) ? payload : [payload],
            };
        },
        [FEATURED_CATALOGS_COMPLETED]: (state, { payload, error }) => {
            if (error) {
                return {
                    ...state,
                    isFetching: false,
                    error,
                };
            }
            return {
                ...state,
                error: undefined,
                isFetching: false,
                featuredEntities: payload.entities,
                featuredResult: payload.result,
            };
        },
        [CATALOGS_RESET]: () => {
            return initialState;
        },
        [CATALOGS.FETCH.START]: (state: IState) => {
            return {
                ...state,
                isFetching: true,
                error: undefined,
                hasMoreResults: false,
                nextPage: undefined,
            };
        },
        [CATALOGS.FETCH.COMPLETED]: (state: IState, { error, meta, payload }: any): IState => {
            if (error) {
                return {
                    ...state,
                    isFetching: false,
                    error: payload,
                    hasMoreResults: false,
                    nextPage: undefined,
                };
            }
            return {
                ...state,
                isFetching: false,
                result: meta.replace ? payload.result : state.result.concat(payload.result),
                nextPage: payload.nextPage,
                hasMoreResults: !!payload.nextPage,
            };
        },
        [CATALOG.ENTRIES.ALL.ADD]: (state: IState, { payload }: any) => {
            const productMap = fromJS({ [payload.catalogId]: { [payload.product]: true } });
            const updatedCache = state.productCache.mergeDeep(productMap);

            return {
                ...state,
                productCache: updatedCache,
            };
        },
        [CATALOG.ENTRIES.ALL.REMOVE]: (state: IState, { payload }: any) => {
            const updatedCache = state.productCache.deleteIn([payload.catalogId, payload.product]);

            return {
                ...state,
                productCache: updatedCache,
            };
        },
        [CATALOG.ENTRIES.ALL.FETCH.START]: (state: IState) => {
            return {
                ...state,
                isFetchingCache: true,
            };
        },
        [CATALOG.ENTRIES.ALL.FETCH.COMPLETED]: (state: IState, { error, meta, payload }: any): IState => {
            if (error) {
                // if we fail to get this, all we lose is the helpful indicator that item is already
                // in catalog, so no need to enter a serious error state
                return {
                    ...state,
                    isFetchingCache: false,
                    error: payload,
                };
            }

            const productCache = { [meta.id]: {} };
            payload.result.forEach((id: string) => {
                // make a dictionary which will dramatically speed up this check in the UI
                productCache[meta.id][id] = true;
            });

            return {
                ...state,
                isFetchingCache: false,
                productCache: fromJS(productCache),
            };
        },
    },
    initialState,
);

export default catalogsReducer;
